// CTABlock.jsx - Fully dynamic, 100% styles from config (no hardcodes)
import React from 'react';
import { ArrowRight, Clock, Gift, Sparkles } from 'lucide-react';

const CTABlock = ({ config = {} }) => {
  const {
    isVisible = true,
    preset = 'split-urgency',

    // Common
    title = 'Limited Offer',
    subtitle = 'Don’t miss out!',
    background = 'from-purple-600 to-pink-600',
    textColor = '#ffffff',

    // Title / Subtitle styles
    titleStyle = { color: '#ffffff', fontSize: 'text-4xl md:text-5xl', fontWeight: 'font-black', fontFamily: 'font-sans' },
    subtitleStyle = { color: '#ffffff', fontSize: 'text-lg', fontWeight: 'font-normal', fontFamily: 'font-sans' },
     overlayColor = 'black',
    overlayOpacity = 65,
    overlayBlur = 'md',
    // Button
    buttonText = 'Shop Now',
    buttonBg = 'bg-white',
    buttonTextColor = '#7c3aed',
    buttonStyle = { fontSize: 'text-lg', fontWeight: 'font-bold', padding: 'py-4 px-8', borderRadius: 'rounded-xl' },

    // Image
    imageUrl = '',

    // Split-urgency specific
    showCountdown = false,
    countdownValues = { days: '02', hours: '14', mins: '35' },
    countdownBoxStyle = { bg: 'bg-white/20', textColor: '#ffffff', borderRadius: 'rounded-lg' },
    badgeText = 'Hot Deal',
    badgeColor = 'bg-yellow-400 text-gray-900',
    badgeStyle = { fontSize: 'text-sm', padding: 'px-4 py-1.5', borderRadius: 'rounded-full' },
    features = [],
    featureIconColor = '#ffffff',
    featureTextColor = '#ffffff',
    featureBoxStyle = { bg: 'bg-white/20', padding: 'p-1.5', borderRadius: 'rounded-full' },

    // Horizontal-banner specific
    showSparkleBadge = true,
    sparkleBadgeText = 'Featured',
    sparkleBadgeStyle = { bg: 'bg-white/20', textColor: '#ffffff', padding: 'px-4 py-1.5', borderRadius: 'rounded-full' },

    // Dark-vip specific
    showBenefits = true,
    benefitsBoxStyle = { bg: 'bg-white/10', padding: 'px-5 py-2.5', borderRadius: 'rounded-lg' },

    // Container from dev mode
    customStyles = { container: {} },
  } = config;

  if (!isVisible) return null;

  const containerClass = customStyles.container?.className || '';
  const containerStyle = {
    padding: customStyles.container?.padding,
    margin: customStyles.container?.margin,
    borderRadius: customStyles.container?.borderRadius,
    boxShadow: customStyles.container?.boxShadow,
  };

  // ── Design 1: Split Image with Urgency ───────────────────────────────
  if (preset === 'split-urgency') {
    return (
      <div
        className={`group relative  overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ${background} ${containerClass}`}
        style={{ ...containerStyle, color: textColor }}
      >
        {imageUrl && (
          <div className="absolute inset-0">
            <img
              src={imageUrl}
              alt="Promotion"
              className="w-full h-full object-cover opacity-85 group-hover:scale-110 transition-transform duration-700"
            />
          </div>
        )}

        <div className="relative p-8 md:p-12">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {showCountdown && (
              <div className="flex gap-3">
                {Object.entries(countdownValues).map(([key, val]) => (
                  <div
                    key={key}
                    className={`${countdownBoxStyle.bg} backdrop-blur-sm ${countdownBoxStyle.borderRadius} p-3 text-center min-w-[70px]`}
                    style={{ color: countdownBoxStyle.textColor }}
                  >
                    <div className="text-2xl font-black">{val}</div>
                    <div className="text-xs uppercase opacity-90">{key}</div>
                  </div>
                ))}
              </div>
            )}
            {badgeText && (
              <div
                className={`${badgeStyle.fontSize} ${badgeStyle.padding} ${badgeStyle.borderRadius} font-bold flex items-center gap-2 ${badgeColor}`}
              >
                <Clock className="w-4 h-4" /> {badgeText}
              </div>
            )}
          </div>

          <h3
            className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-4 leading-tight`}
            style={{ color: titleStyle?.color }}
          >
            {title.split(' ').map((word, i) => (
              <span key={i} className={i % 2 === 1 ? 'block text-yellow-300' : ''}>
                {word}{' '}
              </span>
            ))}
          </h3>

          <p
            className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} ${subtitleStyle.fontFamily} opacity-90 mb-8 max-w-lg`}
            style={{ color: subtitleStyle?.color }}
          >
            {subtitle}
          </p>

          {features.length > 0 && (
            <div className="space-y-3 mb-8">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`${featureBoxStyle.bg} backdrop-blur-sm ${featureBoxStyle.padding} ${featureBoxStyle.borderRadius}`}>
                    <Gift className="w-4 h-4" style={{ color: featureIconColor }} />
                  </div>
                  <span className="font-medium"  style={{ color: titleStyle?.color }}>
                    {f}
                  </span>
                </div>
              ))}
            </div>
          )}

          <button
            className={`${buttonStyle.padding} ${buttonStyle.borderRadius} ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-xl transition-all hover:scale-105 flex items-center justify-center gap-2 ${buttonBg} hover:bg-yellow-300 hover:text-gray-900`}
            style={{ color: buttonTextColor }}
          >
            {buttonText}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="text-sm opacity-70 mt-4">*Terms apply • While supplies last</p>
        </div>
      </div>
    );
  }

  // ── Design 2: Horizontal Banner ──────────────────────────────────────
  if (preset === 'horizontal-banner') {
    return (
      <div
        className={`group relative  overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ${background} ${containerClass}`}
        style={containerStyle}
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative flex flex-col md:flex-row items-center justify-between p-8 md:p-12 gap-8 text-white">
          <div className="flex-1">
            {showSparkleBadge && (
              <div
                className={`${sparkleBadgeStyle.bg} backdrop-blur-sm ${sparkleBadgeStyle.padding} ${sparkleBadgeStyle.borderRadius} text-sm font-bold mb-4 flex items-center gap-2`}
                style={{ color: sparkleBadgeStyle.textColor || '#ffffff' }}
              >
                <Sparkles className="w-4 h-4" /> {sparkleBadgeText}
              </div>
            )}

            <h3
              className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-5`}
              style={{ color: titleStyle?.color }}
            >
              {title}
            </h3>
            <p
              className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} ${subtitleStyle.fontFamily} opacity-90 mb-8 max-w-2xl`}
              style={{ color: subtitleStyle?.color }}
            >
              {subtitle}
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                className={`${buttonStyle.padding} ${buttonStyle.borderRadius} ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-xl transition hover:scale-105 flex items-center gap-2 ${buttonBg}`}
                style={{ color: buttonTextColor }}
              >
                {buttonText}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {imageUrl && (
            <div className="relative w-64 h-64 md:w-80 md:h-80 flex-shrink-0">
              <img
                src={imageUrl}
                alt="Collection"
                className="w-full h-full object-cover  shadow-2xl group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute -top-6 -right-6 bg-yellow-400 text-gray-900 w-20 h-20 rounded-full flex items-center justify-center font-black text-xl shadow-xl animate-bounce">
                NEW
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Design 3: Dark VIP / Membership ──────────────────────────────────
  if (preset === 'dark-vip') {
    return (
      <div
        className={`group relative  overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 bg-gray-900 text-center ${containerClass}`}
        style={containerStyle}
      >
        <div className="relative p-10 md:p-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-5 py-2 rounded-full text-sm font-bold mb-6">
            <Gift className="w-4 h-4" /> Exclusive
          </div>

          <h3
            className={`${titleStyle.fontSize} ${titleStyle.fontWeight} ${titleStyle.fontFamily} mb-5`}
            style={{ color: titleStyle?.color }}
          >
            {title}
          </h3>
          <p
            className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} ${subtitleStyle.fontFamily} text-gray-300 mb-10 max-w-3xl mx-auto`}
            style={{ color: subtitleStyle?.color }}
          >
            {subtitle}
          </p>

          {showBenefits && (
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              {features.map((f, i) => (
                <div
                  key={i}
                  className={`${benefitsBoxStyle.bg} backdrop-blur-sm ${benefitsBoxStyle.padding} ${benefitsBoxStyle.borderRadius} text-white flex items-center gap-2`}
                >
                  <span className="text-xl">{f.icon || '•'}</span>
                  <span className="font-semibold">{f.text || f}</span>
                </div>
              ))}
            </div>
          )}

          <button
            className={`${buttonStyle.padding} ${buttonStyle.borderRadius} ${buttonStyle.fontSize} ${buttonStyle.fontWeight} shadow-2xl transition hover:scale-105 ${buttonBg}`}
            style={{ color: buttonTextColor }}
          >
            {buttonText}
            <ArrowRight className="inline ml-3 w-6 h-6" />
          </button>

          <p className="text-gray-400 text-sm mt-6">No commitment • Cancel anytime</p>
        </div>
      </div>
    );
  }

  return null;
};

export default CTABlock;